import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import useCart from "../hooks/useCart";

export default function Product() {
  const { id } = useParams();
  const [wine, setWine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [toast, setToast] = useState({ open: false, text: "" });

  const { addToCart } = useCart();
  const fmt = useMemo(
    () => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF" }),
    []
  );
  // Remonter en haut de la page quand on arrive sur un produit
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [id]);

  useEffect(() => {
    let active = true;

    const fetchWine = async () => {
      try {
        const snap = await getDoc(doc(db, "winesek", id));
        if (!active) return;
        setWine(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (err) {
        console.error(err);
        setWine(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    if (id) fetchWine();
    return () => {
      active = false;
    };
  }, [id]);

  const priceNumber = Number(wine?.price) || 0;
  const priceText = fmt.format(priceNumber);

  const handleAdd = () => {
    if (!wine) return;
    // PAS de "+" devant l'appel !
    addToCart(
      {
        id: wine.id,
        name: wine.name,
        price: priceNumber,
        imageUrl: wine.imageUrl,
      },
      qty // on passe bien la quantité
    );
    setToast({ open: true, text: `« ${wine.name} » ajouté au panier (${qty})` });
    clearTimeout(handleAdd._t);
    handleAdd._t = setTimeout(() => setToast((t) => ({ ...t, open: false })), 2500);
  };

  const handleWhatsApp = () => {
    if (!wine) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const productUrl = `${origin}/product/${wine.id}`;
    const unit = priceText;
    const total = fmt.format(priceNumber * qty);

    const message = encodeURIComponent(
      `Bonjour 👋, je souhaite commander le vin "${wine.name}" (${qty} bouteille${qty > 1 ? "s" : ""}).\n` +
        `Prix unitaire : ${unit}\nTotal estimé : ${total}\n\n` +
        `Fiche produit : ${productUrl}`
    );

    const phone = "393895513136"; // sans '+'
    window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
  };

  const inc = () => setQty((q) => Math.min(99, q + 1));
  const dec = () => setQty((q) => Math.max(1, q - 1));

  if (loading) {
    return (
      <div className="container py-4">
        <p>Chargement…</p>
      </div>
    );
  }

  if (!wine) {
    return (
      <div className="container py-4">
        <p>Vin introuvable.</p>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row g-4">
        {/* Media */}
        <div className="col-md-5">
          {wine.imageUrl ? (
            <img
              src={wine.imageUrl}
              alt={wine.name}
              className="img-fluid rounded shadow-sm w-100"
              style={{ maxHeight: 480, objectFit: "contain" }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/placeholder.png";
              }}
            />
          ) : (
            <div className="bg-light border rounded" style={{ height: 300 }} />
          )}
        </div>

        {/* Infos */}
        <div className="col-md-7">
          <h1 className="h3">{wine.name}</h1>
          <p className="text-muted mb-1">
            {wine.region || "Région inconnue"}
            {wine.year ? ` • ${wine.year}` : ""}
            {wine.type ? ` • ${String(wine.type).toUpperCase()}` : ""}
          </p>

          <p className="mt-3">{wine.description || "Aucune description."}</p>
          <h4 className="mb-3">{priceText}</h4>

          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <div className="input-group" style={{ width: 140 }}>
              <button type="button" className="btn btn-outline-secondary" onClick={dec}>
                −
              </button>
              <input
                type="number"
                className="form-control text-center"
                value={qty}
                min={1}
                max={99}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setQty(Number.isFinite(n) ? Math.max(1, Math.min(99, n)) : 1);
                }}
              />
              <button type="button" className="btn btn-outline-secondary" onClick={inc}>
                +
              </button>
            </div>

            <button className="btn btn-primary" onClick={handleAdd}>
              Ajouter au panier
            </button>

            <button className="btn btn-success d-flex align-items-center gap-1" onClick={handleWhatsApp}>
              <i className="bi bi-whatsapp" aria-hidden="true" />
              Commander sur WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* Toast panier */}
      <div className={`wc-toast ${toast.open ? "show" : ""}`} role="status" aria-live="polite">
        <div className="wc-toast-body">
          <div className="wc-toast-dot" />
          <div className="flex-grow-1">{toast.text}</div>
          <button
            type="button"
            className="btn-close btn-close-white ms-2"
            aria-label="Fermer"
            onClick={() => setToast((t) => ({ ...t, open: false }))}
          />
        </div>
      </div>
    </div>
  );
}

